import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { apiFetchRaw, extractApiMessage, type ApiRequestOptions } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { offlineDb, type IdMapping, type PendingOperation } from './db';
import type { ParteDelDiaPorCamada } from '@/lib/types';
import {
  currentOwner,
  clearStorageError,
  saveSnapshot,
  notifyOfflineChange,
  reportStorageError,
  reportStorageFailure,
  cacheGeneration,
  getStorageError,
  isTemporaryFailure,
  connectOfflineChannel,
  snapshotId,
} from './cache';
import { getBundle, saveBundle, prepareOfflineBundle, isPreparing, currentBundle } from './bundle';
import { readOffline, projectOperation, scopeForPath, operationResult } from './domain';
import type { OfflineBundle } from './domainTypes';
import { parseEntityId } from '@/lib/entityId';

const ordered = (a: PendingOperation, b: PendingOperation) =>
  a.timestamp - b.timestamp || a.id.localeCompare(b.id);
const rowsFor = async (owner: number) =>
  (await offlineDb.mutations.where('ownerId').equals(owner).toArray()).sort(ordered);
const holder = crypto.randomUUID();
const LEASE_MS = 120_000;
let syncing: Promise<void> | null = null;
let busy = false;
let connection: 'online' | 'offline' | 'unavailable' | 'checking' = 'checking';
export function setServerConnection(value: typeof connection) {
  if (connection === value) return;
  connection = value;
  notifyOfflineChange();
}
const entities: Record<string, string> = {
  camadas: 'camadas',
  naves: 'naves',
  'lotes-nave': 'lotes',
  pesajes: 'pesajes',
  'registros-diarios': 'registros',
  'entregas-pienso': 'entregas',
  salidas: 'salidas',
  'entradas-animales': 'entradas',
};
const foreignIds: Record<string, string> = {
  camada_id: 'camadas',
  camada_recria_origen_id: 'camadas',
  nave_id: 'naves',
  lote_nave_id: 'lotes',
  salida_id: 'salidas',
  granja_origen_id: 'granjas_origen',
  piensotipo_id: 'tipos_pienso',
};
export function isBusinessPath(path: string) {
  return /^\/v1\/(camadas|naves|lotes-nave|catalogos|iot)(?:[/?]|$)/.test(path);
}
export async function checkCurrentReadPermissions(path: string, options: ApiRequestOptions) {
  const method = (options.method ?? 'GET').toUpperCase();
  if (!isBusinessPath(path) || (method !== 'GET' && !/\/liquidacion\/preview$/.test(path))) return;
  const owner = currentOwner();
  if (owner == null) return;
  let fresh: OfflineBundle | null;
  try {
    fresh = await getBundle(owner);
  } catch (error) {
    reportStorageFailure(error);
    return;
  }
  if (!fresh) return;
  try {
    readOffline(
      fresh,
      path,
      typeof options.body === 'string' ? JSON.parse(options.body) : options.body,
    );
  } catch (error) {
    if ((error as { status?: number })?.status === 403) throw error;
  }
}
export async function checkReadGeneration(
  path: string,
  options: ApiRequestOptions,
  generation: string | undefined,
) {
  const method = (options.method ?? 'GET').toUpperCase();
  if (!isBusinessPath(path) || (method !== 'GET' && !/\/liquidacion\/preview$/.test(path))) return;
  const latest = await cacheGeneration(currentOwner());
  if (latest !== generation)
    throw Object.assign(new Error('La copia local ha cambiado. Vuelve a consultar los datos.'), {
      status: 409,
    });
}
export function isAllowedWrite(path: string, method: string) {
  const p = path.split('?')[0];
  const id = '-?\\d+';
  if (method === 'POST' && /\/liquidacion\/preview$/.test(p)) return false;
  return (
    (['POST'].includes(method) && p === '/v1/camadas') ||
    (['PATCH', 'DELETE'].includes(method) && new RegExp(`^/v1/camadas/${id}$`).test(p)) ||
    (method === 'POST' &&
      new RegExp(
        `^/v1/camadas/${id}/(cerrar|reabrir|lotes-nave|registros-diarios|entradas-animales|entregas-pienso|salidas)$`,
      ).test(p)) ||
    (['PATCH', 'PUT', 'DELETE'].includes(method) &&
      new RegExp(
        `^/v1/camadas/${id}/(entradas-animales|entregas-pienso|salidas|lotes-nave)/${id}(/peso)?$`,
      ).test(p)) ||
    (method === 'PUT' && new RegExp(`^/v1/camadas/${id}/liquidacion$`).test(p)) ||
    (['POST', 'PATCH', 'DELETE'].includes(method) && new RegExp(`^/v1/naves(/${id})?$`).test(p)) ||
    (method === 'PUT' && new RegExp(`^/v1/naves/${id}/sensores$`).test(p)) ||
    (['POST', 'PATCH', 'DELETE'].includes(method) &&
      new RegExp(`^/v1/lotes-nave/${id}/(pesajes|registros-diarios)(/${id})?$`).test(p)) ||
    (method === 'PUT' && /^\/v1\/catalogos\/(curva-esperada|curva-pienso|liquidacion)$/.test(p)) ||
    (method === 'PUT' && new RegExp(`^/v1/iot/demo/naves/${id}/scenario$`).test(p))
  );
}
function createEntity(path: string, method: string): string | undefined {
  if (method !== 'POST') return;
  const parts = path.split('?')[0].split('/');
  const last = parts.at(-1)!;
  if (last === 'registros-diarios' && parts[2] === 'camadas') return;
  return entities[last];
}
function affectedScopes(bundle: OfflineBundle, path: string, method: string, primary: string) {
  const scopes = new Set([primary]);
  const p = path.split('?')[0];
  if (
    (/^\/v1\/camadas\/-?\d+(?:\/(cerrar|reabrir))?$/.test(p) &&
      ['POST', 'PATCH', 'DELETE'].includes(method)) ||
    /^\/v1\/camadas\/-?\d+\/entradas-animales(?:\/-?\d+)?$/.test(p)
  )
    scopes.add('camadas');
  if (p === '/v1/catalogos/liquidacion') {
    scopes.add('camadas');
    Object.keys(bundle.revisions)
      .filter((scope) => scope.startsWith('camada:'))
      .forEach((scope) => scopes.add(scope));
  }
  const nave = /^\/v1\/naves\/(-?\d+)(\/sensores)?$/.exec(p);
  if (nave && ((method === 'PATCH' && !nave[2]) || (method === 'PUT' && nave[2])))
    bundle.tables.lotes
      .filter((lote) => lote.nave_id === Number(nave[1]))
      .forEach((lote) => scopes.add(`camada:${lote.camada_id}`));
  return [...scopes];
}
function canonicalScope(scope: string, mappings: IdMapping[]) {
  const match = /^camada:(-\d+)$/.exec(scope);
  return match
    ? `camada:${mappings.find((row) => row.entity === 'camadas' && row.localId === Number(match[1]))?.remoteId ?? match[1]}`
    : scope;
}
function confirmedRevisionKey(explotacionId: number, scope: string, revision: string) {
  return ['offline-confirmed-revision', explotacionId, scope, revision] as const;
}
/** Advance a retained editor only through its device's confirmed writes, never a bootstrap. */
async function confirmedRevision(
  owner: number,
  explotacionId: number,
  scope: string,
  revision: string | null,
): Promise<string | null> {
  if (!revision) return revision;
  let next = revision;
  const visited = new Set<string>();
  while (!visited.has(next)) {
    visited.add(next);
    const proof = await offlineDb.snapshots.get(
      snapshotId(confirmedRevisionKey(explotacionId, scope, next), owner),
    );
    const data = proof?.data as { revision?: unknown } | undefined;
    if (proof?.ownerId !== owner || typeof data?.revision !== 'string' || !data.revision)
      return next;
    next = data.revision;
  }
  return revision;
}
function identityIds(value: unknown): number[] {
  if (Array.isArray(value)) return value.flatMap(identityIds);
  if (!value || typeof value !== 'object') return [];
  return Object.entries(value).flatMap(([key, item]) =>
    key === 'id' && typeof item === 'number' && Number.isSafeInteger(item) && item < 0
      ? [item]
      : identityIds(item),
  );
}
function newlyCreatedIds(before: OfflineBundle, after: OfflineBundle) {
  return Object.entries(after.tables)
    .filter(([entity]) => Object.values(entities).includes(entity))
    .flatMap(([entity, rows]) => {
      const existing = new Set(
        before.tables[entity as keyof OfflineBundle['tables']].map((row) => row.id),
      );
      return rows
        .filter((row) => typeof row.id === 'number' && row.id < 0 && !existing.has(row.id))
        .map((row) => ({
          entity,
          localId: row.id!,
          ...(entity === 'registros'
            ? { naturalKey: { lote_nave_id: row.lote_nave_id, fecha: row.fecha } }
            : {}),
        }));
    });
}
function knownReferences(path: string, body: unknown): Array<{ entity: string; id: number }> {
  const found: Array<{ entity: string; id: number }> = [];
  const segments = path.split('?')[0].split('/');
  segments.forEach((segment, i) => {
    if (entities[segment] && /^-?\d+$/.test(segments[i + 1] ?? ''))
      found.push({ entity: entities[segment], id: Number(segments[i + 1]) });
  });
  const walk = (value: unknown) => {
    if (Array.isArray(value)) value.forEach(walk);
    else if (value && typeof value === 'object')
      Object.entries(value).forEach(([key, item]) => {
        if (foreignIds[key] && typeof item === 'number')
          found.push({ entity: foreignIds[key], id: item });
        else if (Array.isArray(item) || (item && typeof item === 'object')) walk(item);
      });
  };
  walk(body);
  return found;
}
export function rewriteReferences(
  path: string,
  body: unknown,
  mappings: IdMapping[],
): { path: string; body: unknown } {
  const resolve = (entity: string, id: number) =>
    mappings.find((m) => m.entity === entity && m.localId === id)?.remoteId ?? id;
  const [route, query] = path.split('?');
  const segments = route.split('/');
  segments.forEach((segment, i) => {
    if (entities[segment] && /^-?\d+$/.test(segments[i + 1] ?? ''))
      segments[i + 1] = String(resolve(entities[segment], Number(segments[i + 1])));
  });
  const walk = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(walk);
    if (value && typeof value === 'object')
      return Object.fromEntries(
        Object.entries(value).map(([key, item]) => [
          key,
          foreignIds[key] && typeof item === 'number' ? resolve(foreignIds[key], item) : walk(item),
        ]),
      );
    return value;
  };
  return { path: segments.join('/') + (query === undefined ? '' : `?${query}`), body: walk(body) };
}
async function projected(owner: number, base: OfflineBundle) {
  const operations = (await rowsFor(owner)).filter(
    (operation) => operation.explotacionId === base.explotacion_id,
  );
  const mappings = (await offlineDb.mappings.where('ownerId').equals(owner).toArray()).filter(
    (m) => m.explotacionId === base.explotacion_id,
  );
  let result = base;
  const projectionErrors = new Map<string, string>();
  for (const operation of operations) {
    if (operation.dependencies.some((id) => projectionErrors.has(id))) {
      projectionErrors.set(
        operation.id,
        'Cambio retenido sin aplicar: depende de otro cambio que necesita revisión.',
      );
      continue;
    }
    try {
      result = projectOperation(result, {
        ...operation,
        ...rewriteReferences(operation.path, operation.body, mappings),
      });
    } catch (error) {
      // A refreshed generation may invalidate a retained draft. Keep that
      // draft intact and keep unrelated screens readable.
      projectionErrors.set(
        operation.id,
        `Cambio retenido sin aplicar: ${extractApiMessage(error)}`,
      );
    }
  }
  return { bundle: result, operations, mappings, projectionErrors };
}
export async function readProjectedBundle(): Promise<OfflineBundle | null> {
  const owner = currentOwner();
  if (owner == null) return null;
  const base = await getBundle(owner);
  return base ? (await projected(owner, base)).bundle : null;
}
export async function enqueueOperation(
  path: string,
  method: string,
  body: unknown,
  viewed?: OfflineBundle | null,
) {
  const owner = currentOwner();
  if (owner == null) throw new Error('Inicia sesión para guardar cambios.');
  let result: unknown;
  try {
    await offlineDb.transaction(
      'rw',
      offlineDb.bundles,
      offlineDb.mutations,
      offlineDb.mappings,
      offlineDb.snapshots,
      async () => {
        const base = await getBundle(owner);
        if (!base)
          throw new Error('Prepara la aplicación con conexión antes de guardar sin conexión.');
        if (viewed && (viewed.owner_id !== owner || viewed.explotacion_id !== base.explotacion_id))
          throw new Error('La sesión o explotación ha cambiado.');
        const context = await projected(owner, base);
        const rewritten = rewriteReferences(path, body, context.mappings);
        const scope = scopeForPath(context.bundle, rewritten.path, method);
        const source = viewed ?? base;
        const ownCreation = !source.revisions[scope]
          ? context.mappings.find(
              (mapping) => mapping.entity === 'camadas' && scope === `camada:${mapping.remoteId}`,
            )
          : undefined;
        const scopes = affectedScopes(context.bundle, rewritten.path, method, scope);
        const guards = (operation: PendingOperation) =>
          (operation.affectedScopes ?? [operation.scope]).map((guarded) =>
            canonicalScope(guarded, context.mappings),
          );
        const previous = context.operations.filter((o) => guards(o).includes(scope)).at(-1);
        if (
          previous &&
          (['error', 'conflict'].includes(previous.status) ||
            context.projectionErrors.has(previous.id))
        )
          throw new Error('Resuelve el cambio pendiente antes de guardar otra edición.');
        const refs = knownReferences(path, body).filter((r) => r.id < 0);
        const dependencies = refs
          .map((ref) => {
            const mapping = context.mappings.find(
              (m) => m.entity === ref.entity && m.localId === ref.id,
            );
            if (mapping) return null;
            const creator = context.operations.find(
              (o) =>
                (o.localId === ref.id && o.entity === ref.entity) ||
                o.createdIds?.some(
                  (created) => created.localId === ref.id && created.entity === ref.entity,
                ),
            );
            if (!creator) throw new Error('El registro local relacionado ya no está disponible.');
            return creator.id;
          })
          .filter((id): id is string => id !== null);
        if (previous) dependencies.push(previous.id);
        const versionParents = Object.fromEntries(
          scopes.flatMap((guarded) => {
            const predecessor = context.operations
              .filter((o) => guards(o).includes(guarded))
              .at(-1);
            if (!predecessor) return [];
            if (
              ['error', 'conflict'].includes(predecessor.status) ||
              context.projectionErrors.has(predecessor.id)
            )
              throw new Error('Resuelve el cambio pendiente antes de guardar otra edición.');
            dependencies.push(predecessor.id);
            return [[guarded, predecessor.id]];
          }),
        );
        const lifecycle = /^\/v1\/camadas\/(-?\d+)(?:\/(cerrar|reabrir))?$/.exec(
          path.split('?')[0],
        );
        if (lifecycle && (method === 'DELETE' || lifecycle[2]))
          dependencies.push(
            ...context.operations
              .filter(
                (o) =>
                  canonicalScope(o.scope, context.mappings) ===
                  canonicalScope(`camada:${lifecycle[1]}`, context.mappings),
              )
              .map((o) => o.id),
          );
        const approvedSnapshot =
          body && typeof body === 'object'
            ? (body as Record<string, unknown>).approved_snapshot
            : undefined;
        const cleanBody =
          body && typeof body === 'object' ? { ...(body as Record<string, unknown>) } : body;
        if (cleanBody && typeof cleanBody === 'object')
          delete (cleanBody as Record<string, unknown>).approved_snapshot;
        const reservations = await rowsFor(owner);
        const allMappings = await offlineDb.mappings.where('ownerId').equals(owner).toArray();
        const localId =
          Math.min(
            0,
            ...identityIds(context.bundle),
            ...reservations.flatMap((o) => [
              o.localId ?? 0,
              ...(o.createdIds ?? []).map((created) => created.localId),
            ]),
            ...allMappings.map((m) => m.localId),
          ) - 1;
        const contextRevisions: Record<string, string> = {};
        for (const guarded of scopes.filter((guarded) => guarded !== scope)) {
          const revision = ownCreation?.guardedContexts?.[guarded] ?? source.revisions[guarded];
          if (!revision && guarded.includes(':-') && versionParents[guarded]) continue;
          if (!revision) throw new Error('Falta preparar el contexto de negocio con conexión.');
          contextRevisions[guarded] = (await confirmedRevision(
            owner,
            base.explotacion_id,
            canonicalScope(guarded, context.mappings),
            revision,
          ))!;
        }
        const operation: PendingOperation = {
          id: crypto.randomUUID(),
          ownerId: owner,
          explotacionId: base.explotacion_id,
          method,
          path,
          body: cleanBody,
          scope,
          expectedRevision: await confirmedRevision(
            owner,
            base.explotacion_id,
            scope,
            source.revisions[scope] ?? ownCreation?.guardedRevision ?? null,
          ),
          dependencies: [...new Set(dependencies)],
          parentId: previous?.id ?? null,
          timestamp: Math.max(Date.now(), (context.operations.at(-1)?.timestamp ?? 0) + 1),
          createdAt: new Date().toISOString(),
          status: 'pending',
          attempts: 0,
          localId,
          entity: createEntity(path, method),
          approvedSnapshot,
          label: `${method} ${path}`,
          affectedScopes: scopes,
          versionParents,
          contextRevisions,
        };
        const local = projectOperation(context.bundle, {
          ...operation,
          ...rewriteReferences(path, cleanBody, context.mappings),
        });
        result = operationResult(local, {
          ...operation,
          ...rewriteReferences(path, cleanBody, context.mappings),
        });
        operation.createdIds = newlyCreatedIds(context.bundle, local);
        if (currentOwner() !== owner) throw new Error('La sesión ha cambiado.');
        await offlineDb.mutations.put(operation);
      },
    );
  } catch (error) {
    reportStorageFailure(error);
    throw error;
  }
  clearStorageError();
  notifyOfflineChange();
  window.dispatchEvent(new Event('offline:queued'));
  return result;
}

const READ_REFRESH_MS = 30_000;
// Refresh attempts and reading freshness differ: IoT readings stay current for
// the same three-minute window used by currentSensorData, while requests poll sooner.
const TELEMETRY_FRESH_MS = 180_000;
const TELEMETRY_READ_PATH = /^\/v1\/iot\/naves\/\d+\/(sensores|temperatura)$/;
interface ReadRefresh {
  generation: string | undefined;
  settledAt: number;
  pending: boolean;
  denial?: unknown;
  unavailable?: boolean;
}
// One entry per route, rather than one per bootstrap UUID. A new generation
// replaces the entry and late responses cannot publish into its successor.
const readRefreshes = new Map<string, ReadRefresh>();
let observingReadOwner = false;
function observeReadOwner() {
  if (observingReadOwner) return;
  observingReadOwner = true;
  useAuthStore.subscribe((state, previous) => {
    if (state.user?.id !== previous.user?.id) readRefreshes.clear();
  });
}
function readRefreshKey(owner: number, farm: number, path: string, body: unknown) {
  return JSON.stringify([owner, farm, path, body ?? null]);
}
function readDenialKey(path: string, body: unknown) {
  return ['api-path', path, body ?? null, 'denial'];
}
async function persistedReadDenial(owner: number, farm: number, path: string, body: unknown) {
  const snapshot = await offlineDb.snapshots
    .get(snapshotId(readDenialKey(path, body), owner))
    .catch((error) => {
      reportStorageFailure(error);
      return undefined;
    });
  const denial = snapshot?.data as
    { explotacion_id?: number; status?: number; message?: string } | undefined;
  return snapshot?.ownerId === owner &&
    denial?.explotacion_id === farm &&
    [401, 403, 404].includes(denial.status ?? 0)
    ? Object.assign(new Error(denial.message), { status: denial.status })
    : undefined;
}
function revalidateRead(
  owner: number,
  base: OfflineBundle,
  generation: string | undefined,
  path: string,
  body: unknown,
  options: ApiRequestOptions,
  localValue: unknown,
  knownDenial?: unknown,
) {
  const key = readRefreshKey(owner, base.explotacion_id, path, body);
  const previous = readRefreshes.get(key);
  if (
    navigator.onLine === false ||
    (previous &&
      previous.generation === generation &&
      (previous.pending || Date.now() - previous.settledAt < READ_REFRESH_MS))
  )
    return;
  const refresh: ReadRefresh = {
    generation,
    settledAt: 0,
    pending: true,
    denial: previous?.denial ?? knownDenial,
    unavailable: previous?.unavailable,
  };
  readRefreshes.set(key, refresh);
  void (async () => {
    try {
      const value = await apiFetchRaw(path, { ...options, expectedOwnerId: owner });
      await checkCurrentReadPermissions(path, options);
      await checkReadGeneration(path, options, generation);
      if (currentOwner() !== owner || readRefreshes.get(key) !== refresh) return;
      let changed = TELEMETRY_READ_PATH.test(path) && refresh.unavailable === true;
      refresh.unavailable = false;
      setServerConnection('online');
      await offlineDb.transaction(
        'rw',
        offlineDb.bundles,
        offlineDb.snapshots,
        offlineDb.mutations,
        async () => {
          if (currentOwner() !== owner || (await cacheGeneration(owner)) !== generation) return;
          changed = refresh.denial !== undefined || changed;
          await offlineDb.snapshots.delete(snapshotId(readDenialKey(path, body), owner));
          refresh.denial = undefined;
          if (
            (await rowsFor(owner)).some(
              (operation) => operation.explotacionId === base.explotacion_id,
            )
          )
            return;
          const snapshotKey = ['api-path', path, body ?? null];
          const before = await offlineDb.snapshots.get(snapshotId(snapshotKey, owner));
          const priorValue = before && before.generation === generation ? before.data : localValue;
          // IoT observers also render snapshot freshness. An unchanged successful
          // revalidation must publish the transition from retained to current data.
          const renewedTelemetry =
            path.startsWith('/v1/iot/') &&
            (!before ||
              before.generation !== generation ||
              Date.now() - before.savedAt > READ_REFRESH_MS);
          changed =
            JSON.stringify(priorValue) !== JSON.stringify(value) || renewedTelemetry || changed;
          await saveSnapshot(snapshotKey, value, owner, generation ?? null);
          refresh.denial = undefined;
        },
      );
      if (changed) notifyOfflineChange();
    } catch (error) {
      if (options.signal?.aborted) return;
      if (
        currentOwner() !== owner ||
        readRefreshes.get(key) !== refresh ||
        (await cacheGeneration(owner)) !== generation
      )
        return;
      if ([401, 403, 404].includes((error as { status?: number })?.status ?? 0)) {
        const newlyDenied = refresh.denial === undefined;
        refresh.denial = error;
        await saveSnapshot(
          readDenialKey(path, body),
          {
            explotacion_id: base.explotacion_id,
            status: (error as { status: number }).status,
            message: extractApiMessage(error),
          },
          owner,
          generation ?? null,
        );
        if (newlyDenied) {
          window.dispatchEvent(new Event('offline:permissions-changed'));
          notifyOfflineChange();
        }
      } else if (isTemporaryFailure(error)) {
        refresh.unavailable = true;
        setServerConnection(navigator.onLine === false ? 'offline' : 'unavailable');
      } else reportStorageFailure(error);
    } finally {
      refresh.pending = false;
      refresh.settledAt = Date.now();
    }
  })().catch(reportStorageFailure);
}

/** All private API callers share the same local sources, including raw useQueries. */
export async function handleOfflineRequest<T>(
  path: string,
  options: ApiRequestOptions,
): Promise<{ handled: boolean; value?: T }> {
  const method = (options.method ?? 'GET').toUpperCase();
  if (!isBusinessPath(path) || options.auth === false || !offlineDb.bundles)
    return { handled: false };
  const owner = currentOwner();
  if (owner == null) return { handled: false };
  observeReadOwner();
  const generation = await cacheGeneration(owner).catch(() => undefined);
  let base: OfflineBundle | null;
  try {
    base = await getBundle(owner);
  } catch (error) {
    reportStorageError(error);
    return { handled: false };
  }
  if (!base) return { handled: false };
  if (options.expectedOwnerId !== undefined && options.expectedOwnerId !== owner)
    throw new Error('La sesión ha cambiado.');
  let body: unknown =
    options.body == null
      ? undefined
      : typeof options.body === 'string'
        ? JSON.parse(options.body)
        : options.body;
  if (isAllowedWrite(path, method)) {
    const value = await enqueueOperation(path, method, body, options.offlineBase);
    // Saving means durable locally. Sending runs separately; quota or network
    // failures cannot turn a successfully retained edit into an unretained one.
    return { handled: true, value: value as T };
  }
  if (method !== 'GET' && !/\/liquidacion\/preview$/.test(path)) return { handled: false };
  const context = await projected(owner, base);
  const canonical = rewriteReferences(path, body, context.mappings);
  body = canonical.body;
  const refresh = readRefreshes.get(
    readRefreshKey(owner, base.explotacion_id, canonical.path, body),
  );
  const denial =
    refresh?.denial ??
    (await persistedReadDenial(owner, base.explotacion_id, canonical.path, body));
  const local = () => readOffline(context.bundle, canonical.path, body);
  let localValue: unknown;
  let localMissing: unknown;
  try {
    localValue = local();
  } catch (error) {
    if (
      (error as { status?: number })?.status !== 404 ||
      context.operations.length ||
      /(?:^|\/)-\d+(?:\/|$)/.test(canonical.path.split('?')[0])
    )
      throw error;
    localMissing = error;
  }
  if (denial) {
    if (!context.operations.length)
      revalidateRead(owner, base, generation, canonical.path, body, options, localValue, denial);
    throw denial;
  }
  if (context.operations.length || (method === 'POST' && navigator.onLine === false)) {
    const value = localValue;
    if (value !== undefined) {
      if (navigator.onLine === false) setServerConnection('offline');
      return { handled: true, value: markCached(value, canonical.path, base) as T };
    }
  }
  {
    const snapshot = await offlineDb.snapshots
      .get(snapshotId(['api-path', canonical.path, body ?? null], owner))
      .catch((error) => {
        reportStorageFailure(error);
        return undefined;
      });
    const cached =
      snapshot?.ownerId === owner && snapshot.generation === generation ? snapshot : undefined;
    if (!context.operations.length && (cached || localValue !== undefined))
      revalidateRead(owner, base, generation, canonical.path, body, options, localValue);
    if (cached || localValue !== undefined) {
      await checkCurrentReadPermissions(canonical.path, options);
      const persistedDenial = await persistedReadDenial(
        owner,
        base.explotacion_id,
        canonical.path,
        body,
      );
      await checkReadGeneration(canonical.path, options, generation);
      if (currentOwner() !== owner)
        throw Object.assign(new Error('La sesión ha cambiado.'), { status: 401 });
      const latestRefresh = readRefreshes.get(
        readRefreshKey(owner, base.explotacion_id, canonical.path, body),
      );
      const latestDenial = latestRefresh?.denial ?? persistedDenial;
      if (latestDenial) throw latestDenial;
      if (navigator.onLine === false) setServerConnection('offline');
      const telemetry = TELEMETRY_READ_PATH.test(canonical.path);
      const live =
        cached &&
        connection === 'online' &&
        navigator.onLine !== false &&
        (!telemetry || !latestRefresh?.unavailable) &&
        Date.now() - cached.savedAt <= (telemetry ? TELEMETRY_FRESH_MS : READ_REFRESH_MS);
      const value = cached
        ? live
          ? cached.data
          : markCached(cached.data, canonical.path, new Date(cached.savedAt).toISOString())
        : markCached(localValue, canonical.path, base.prepared_at);
      return { handled: true, value: value as T };
    }
  }
  if (localMissing && navigator.onLine === false) throw localMissing;
  try {
    const value = await apiFetchRaw<T>(canonical.path, { ...options, expectedOwnerId: owner });
    await checkCurrentReadPermissions(canonical.path, options);
    await checkReadGeneration(canonical.path, options, generation);
    setServerConnection('online');
    try {
      await saveSnapshot(
        ['api-path', canonical.path, body ?? null],
        value,
        owner,
        generation ?? null,
      );
    } catch (error) {
      if ((error as { status?: number })?.status) throw error;
      reportStorageFailure(error);
    }
    return { handled: true, value };
  } catch (error) {
    if (!isTemporaryFailure(error) || currentOwner() !== owner) throw error;
    setServerConnection(navigator.onLine === false ? 'offline' : 'unavailable');
    const fresh = await getBundle(owner);
    const value =
      fresh && fresh.explotacion_id !== base.explotacion_id
        ? readOffline((await projected(owner, fresh)).bundle, path, body)
        : local();
    if (value !== undefined)
      return { handled: true, value: markCached(value, canonical.path, base.prepared_at) as T };
    throw error;
  }
}
function markCached(value: unknown, path: string, cachedAt: string | OfflineBundle) {
  return path.startsWith('/v1/iot/') && value && typeof value === 'object'
    ? {
        ...value,
        local_snapshot: true,
        cached_at: typeof cachedAt === 'string' ? cachedAt : cachedAt.prepared_at,
      }
    : value;
}

interface Receipt {
  operation_uuid: string;
  result: unknown;
  scope: string;
  revision: string;
  bundle: OfflineBundle;
  status_code: number;
  revisions?: Record<string, string>;
}
async function runSync() {
  const owner = currentOwner();
  if (owner == null || !offlineDb.mutations || navigator.onLine === false) return;
  const initial = await rowsFor(owner);
  let current = await getBundle(owner);
  const active = (operation: PendingOperation) =>
    !current || operation.explotacionId === current.explotacion_id;
  if (
    !initial.some(
      (o) => active(o) && !o.dependencies.length && !['error', 'conflict'].includes(o.status),
    )
  )
    return;
  const leaseId = `sync:${owner}`;
  const claimed = await offlineDb.transaction('rw', offlineDb.leases, async () => {
    const lease = await offlineDb.leases.get(leaseId);
    if (lease && lease.expiresAt > Date.now() && lease.holder !== holder) return false;
    await offlineDb.leases.put({ id: leaseId, holder, expiresAt: Date.now() + LEASE_MS });
    return true;
  });
  if (!claimed) return;
  busy = true;
  notifyOfflineChange();
  const heartbeat = window.setInterval(
    () =>
      void offlineDb
        .transaction('rw', offlineDb.leases, async () => {
          if ((await offlineDb.leases.get(leaseId))?.holder === holder)
            await offlineDb.leases.put({ id: leaseId, holder, expiresAt: Date.now() + LEASE_MS });
        })
        .catch(reportStorageError),
    30_000,
  );
  const attempted = new Set<string>();
  let confirmedAny = false;
  try {
    while (currentOwner() === owner) {
      current = await getBundle(owner);
      const operation = (await rowsFor(owner)).find(
        (o) =>
          active(o) &&
          !o.dependencies.length &&
          !attempted.has(o.id) &&
          !['error', 'conflict'].includes(o.status),
      );
      if (!operation) break;
      attempted.add(operation.id);
      const sending = await offlineDb.transaction(
        'rw',
        offlineDb.mutations,
        offlineDb.leases,
        offlineDb.mappings,
        offlineDb.bundles,
        async () => {
          const lease = await offlineDb.leases.get(leaseId);
          const row = await offlineDb.mutations.get(operation.id);
          if (
            lease?.holder !== holder ||
            lease.expiresAt <= Date.now() ||
            !row ||
            row.dependencies.length ||
            ['error', 'conflict'].includes(row.status)
          )
            return null;
          let payload = row.payload;
          if (!payload) {
            const bundle = await getBundle(owner);
            if (!bundle || bundle.explotacion_id !== row.explotacionId)
              throw new Error('La explotación ha cambiado.');
            const mappings = (
              await offlineDb.mappings.where('ownerId').equals(owner).toArray()
            ).filter((m) => m.explotacionId === row.explotacionId);
            const mapped = rewriteReferences(row.path, row.body, mappings);
            if (knownReferences(mapped.path, mapped.body).some((ref) => ref.id < 0))
              throw new Error('Falta confirmar un registro relacionado.');
            const scope = scopeForPath(bundle, mapped.path, row.method);
            const expectedRevision =
              row.expectedRevision ??
              mappings.find(
                (mapping) => mapping.entity === 'camadas' && scope === `camada:${mapping.remoteId}`,
              )?.guardedRevision ??
              null;
            if (!expectedRevision)
              throw new Error(
                'Falta la revisión del contexto confirmado. Prepara la aplicación con conexión.',
              );
            payload = {
              operation_uuid: row.id,
              expected_explotacion_id: row.explotacionId,
              expected_revision: expectedRevision,
              method: row.method,
              path: mapped.path,
              body: mapped.body,
              ...(row.approvedSnapshot ? { approved_snapshot: row.approvedSnapshot } : {}),
              ...(row.contextRevisions && Object.keys(row.contextRevisions).length
                ? {
                    context_revisions: Object.fromEntries(
                      Object.entries(row.contextRevisions).map(([scope, revision]) => [
                        canonicalScope(scope, mappings),
                        revision,
                      ]),
                    ),
                  }
                : {}),
            };
          }
          const next = {
            ...row,
            payload,
            status: 'sending' as const,
            attempts: row.attempts + 1,
            message: undefined,
          };
          await offlineDb.mutations.put(next);
          return next;
        },
      );
      if (!sending) break;
      notifyOfflineChange();
      let confirmed = false;
      try {
        const receipt = await apiFetchRaw<Receipt>('/v1/offline/operations', {
          method: 'POST',
          body: JSON.stringify(sending.payload),
          expectedOwnerId: owner,
        });
        setServerConnection('online');
        if (
          receipt.operation_uuid !== sending.id ||
          receipt.bundle.explotacion_id !== sending.explotacionId
        )
          throw new Error('El servidor devolvió una confirmación incompatible.');
        confirmed = true;
        // Replay may carry newer sources. Guards always describe the original
        // commit, never external changes that arrived after the lost response.
        const committedRevisions = receipt.revisions ?? receipt.bundle.revisions;
        const applied = await offlineDb.transaction(
          'rw',
          offlineDb.mutations,
          offlineDb.bundles,
          offlineDb.mappings,
          offlineDb.leases,
          offlineDb.snapshots,
          async () => {
            const lease = await offlineDb.leases.get(leaseId);
            if (
              lease?.holder !== holder ||
              lease.expiresAt <= Date.now() ||
              currentOwner() !== owner ||
              !(await offlineDb.mutations.get(sending.id))
            )
              return false;
            const current = await getBundle(owner);
            if (current && current.explotacion_id !== sending.explotacionId) return false;
            await saveBundle(receipt.bundle, owner);
            if (
              sending.entity &&
              sending.localId &&
              receipt.result &&
              typeof receipt.result === 'object'
            ) {
              const remoteId = (receipt.result as { id?: number }).id;
              if (!remoteId || remoteId < 0)
                throw new Error('Falta el identificador confirmado del registro.');
              await offlineDb.mappings.put({
                id: `${owner}:${sending.explotacionId}:${sending.entity}:${sending.localId}`,
                ownerId: owner,
                explotacionId: sending.explotacionId,
                entity: sending.entity,
                localId: sending.localId,
                remoteId,
                operationId: sending.id,
                guardedRevision:
                  sending.entity === 'camadas'
                    ? committedRevisions[`camada:${remoteId}`]
                    : undefined,
                guardedContexts: Object.fromEntries(
                  (sending.affectedScopes ?? [sending.scope])
                    .map((scope) => [scope, committedRevisions[scope]])
                    .filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
                ),
              });
            }
            await offlineDb.mutations.delete(sending.id);
            const mappings = (
              await offlineDb.mappings.where('ownerId').equals(owner).toArray()
            ).filter((mapping) => mapping.explotacionId === sending.explotacionId);
            for (const created of sending.createdIds ?? []) {
              if (created.entity === sending.entity && created.localId === sending.localId)
                continue;
              if (created.entity !== 'registros' || !created.naturalKey) continue;
              const key = rewriteReferences('', created.naturalKey, mappings).body as {
                lote_nave_id: number;
                fecha: string;
              };
              const result = receipt.result as {
                fecha?: string;
                registros?: Array<{ id: number | null; lote_nave_id: number; fecha?: string }>;
              };
              const confirmed = result?.registros?.find(
                (row) =>
                  row.lote_nave_id === key.lote_nave_id &&
                  (row.fecha ?? result.fecha) === key.fecha,
              );
              if (!confirmed?.id)
                throw new Error('Falta confirmar el identificador del parte local.');
              await offlineDb.mappings.put({
                id: `${owner}:${sending.explotacionId}:${created.entity}:${created.localId}`,
                ownerId: owner,
                explotacionId: sending.explotacionId,
                entity: created.entity,
                localId: created.localId,
                remoteId: confirmed.id,
                operationId: sending.id,
              });
            }
            for (const child of await rowsFor(owner)) {
              if (!child.dependencies.includes(sending.id)) continue;
              const sameScope =
                child.versionParents?.[child.scope] === sending.id ||
                (child.parentId === sending.id &&
                  canonicalScope(child.scope, mappings) ===
                    canonicalScope(sending.scope, mappings));
              const nextRevision =
                committedRevisions[canonicalScope(child.scope, mappings)] ?? receipt.revision;
              const contexts = { ...child.contextRevisions };
              for (const [scope, parent] of Object.entries(child.versionParents ?? {}))
                if (parent === sending.id && scope !== child.scope) {
                  const revision = committedRevisions[canonicalScope(scope, mappings)];
                  if (revision) contexts[scope] = revision;
                }
              await offlineDb.mutations.put({
                ...child,
                dependencies: child.dependencies.filter((id) => id !== sending.id),
                parentId: child.parentId === sending.id ? null : child.parentId,
                expectedRevision: sameScope ? nextRevision : child.expectedRevision,
                contextRevisions: contexts,
                // Legacy day chains carry their own original receipt protection.
                body:
                  sameScope && child.legacy && receipt.result && typeof receipt.result === 'object'
                    ? {
                        ...(child.body as object),
                        expected_revision: (receipt.result as { revision: string }).revision,
                      }
                    : child.body,
                payload:
                  sameScope && child.payload && child.attempts === 0
                    ? {
                        ...child.payload,
                        body:
                          child.legacy && receipt.result
                            ? {
                                ...(child.payload.body as object),
                                expected_revision: (receipt.result as { revision: string })
                                  .revision,
                              }
                            : child.payload.body,
                        expected_revision: child.legacy ? null : nextRevision,
                      }
                    : child.payload,
              });
            }
            if (
              current &&
              !sending.legacy &&
              JSON.stringify([...current.unavailable].sort()) ===
                JSON.stringify([...receipt.bundle.unavailable].sort())
            ) {
              const primary = canonicalScope(sending.scope, mappings);
              for (const [scope, expected] of Object.entries({
                ...sending.payload?.context_revisions,
                [primary]: sending.payload?.expected_revision,
              })) {
                // A replay's bundle may contain later external edits. Only the
                // immutable receipt proves the revision committed by this write.
                const revision =
                  receipt.revisions?.[scope] ?? (scope === primary ? receipt.revision : undefined);
                if (!expected || !revision || expected === revision) continue;
                const key = confirmedRevisionKey(sending.explotacionId, scope, expected);
                await offlineDb.snapshots.put({
                  id: snapshotId(key, owner),
                  ownerId: owner,
                  key: JSON.stringify(key),
                  data: { revision, operationId: sending.id },
                  savedAt: Date.now(),
                });
              }
            }
            return true;
          },
        );
        if (applied) {
          confirmedAny = true;
          await getBundle(owner);
          clearStorageError();
        }
      } catch (error) {
        if (confirmed) reportStorageError(error);
        const e = error as {
          status?: number;
          body?: {
            detail?: {
              actual?: OfflineBundle;
              bundle?: OfflineBundle;
              legacy_actual?: ParteDelDiaPorCamada;
            };
          };
        };
        const actual = e.body?.detail?.bundle ?? e.body?.detail?.actual;
        const status =
          e.status === 409 && actual
            ? 'conflict'
            : e.status === 401
              ? 'auth'
              : confirmed || isTemporaryFailure(error)
                ? 'retry'
                : 'error';
        if (status === 'retry') setServerConnection(navigator.onLine ? 'unavailable' : 'offline');
        await offlineDb.transaction(
          'rw',
          offlineDb.mutations,
          offlineDb.leases,
          offlineDb.bundles,
          async () => {
            const fresh = await offlineDb.mutations.get(sending.id);
            if ((await offlineDb.leases.get(leaseId))?.holder !== holder || !fresh) return;
            const activeFarm = (await offlineDb.bundles.get(String(owner)))?.data.explotacion_id;
            if (activeFarm && activeFarm !== fresh.explotacionId) return;
            await offlineDb.mutations.put({
              ...sending,
              status,
              message: extractApiMessage(error, 'No se pudo sincronizar.'),
              actual: status === 'conflict' ? actual : undefined,
              legacyActual: e.body?.detail?.legacy_actual ?? sending.legacyActual,
            });
          },
        );
        if (status === 'retry' || status === 'auth') break;
      } finally {
        notifyOfflineChange();
      }
    }
  } finally {
    window.clearInterval(heartbeat);
    await offlineDb.transaction('rw', offlineDb.leases, async () => {
      if ((await offlineDb.leases.get(leaseId))?.holder === holder)
        await offlineDb.leases.delete(leaseId);
    });
    busy = false;
    notifyOfflineChange();
  }
  if (confirmedAny && !(await rowsFor(owner)).filter(active).length)
    await prepareOfflineBundle().catch(() => {});
}
export function syncPendingOperations(): Promise<void> {
  if (syncing) return syncing;
  syncing = runSync()
    .catch((error) => {
      reportStorageFailure(error);
      throw error;
    })
    .finally(() => {
      syncing = null;
      busy = false;
    });
  return syncing;
}
function dependentChain(rows: PendingOperation[], id: string) {
  const ids = new Set([id]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const row of rows)
      if (!ids.has(row.id) && row.dependencies.some((parent) => ids.has(parent))) {
        ids.add(row.id);
        changed = true;
      }
  }
  return rows.filter((row) => ids.has(row.id));
}
export async function resolveOperationConflict(
  id: string,
  choice: 'server' | 'local',
  options: { confirmSensorDeactivation?: boolean } = {},
) {
  const owner = currentOwner();
  if (owner == null) throw new Error('La sesión ha cambiado.');
  await offlineDb.transaction(
    'rw',
    offlineDb.mutations,
    offlineDb.bundles,
    offlineDb.snapshots,
    offlineDb.mappings,
    async () => {
      const rows = await rowsFor(owner);
      const row = rows.find((o) => o.id === id);
      if (!row || row.status !== 'conflict' || !row.actual)
        throw new Error('El conflicto ya no está disponible.');
      if (row.actual.explotacion_id !== row.explotacionId)
        throw new Error('La explotación ha cambiado.');
      const mappings = (await offlineDb.mappings.where('ownerId').equals(owner).toArray()).filter(
        (mapping) => mapping.explotacionId === row.explotacionId,
      );
      const chain = dependentChain(rows, id);
      if (chain.some((o) => o.status === 'sending'))
        throw new Error('Espera a que termine el envío.');
      const sensorConflict =
        choice === 'local' &&
        row.method === 'PUT' &&
        /^\/v1\/naves\/-?\d+\/sensores$/.test(row.path.split('?')[0]);
      const localBody =
        sensorConflict && options.confirmSensorDeactivation === true
          ? { ...(row.body as object), confirm_deactivation: true }
          : row.body;
      if (sensorConflict)
        projectOperation(row.actual, {
          ...row,
          ...rewriteReferences(row.path, localBody, mappings),
        });
      await saveBundle(row.actual, owner);
      for (const item of chain) await offlineDb.mutations.delete(item.id);
      if (choice === 'local') {
        // Explicit user acceptance creates fresh UUIDs; previously attempted
        // requests and their idempotency receipts remain immutable.
        const replacements = new Map(chain.map((item) => [item.id, crypto.randomUUID()]));
        for (const item of chain) {
          const replacementId = replacements.get(item.id)!;
          const body = item.id === row.id ? localBody : item.body;
          const legacyBody = item.legacy
            ? {
                ...(body as object),
                operation_uuid: replacementId,
                ...(row.legacyActual &&
                (item.body as { fecha?: string })?.fecha === row.legacyActual.fecha
                  ? { expected_revision: row.legacyActual.revision }
                  : {}),
              }
            : body;
          await offlineDb.mutations.put({
            ...item,
            id: replacementId,
            body: legacyBody,
            payload: item.legacy
              ? {
                  operation_uuid: replacementId,
                  expected_explotacion_id: item.explotacionId,
                  expected_revision: null,
                  method: item.method,
                  path: item.path,
                  body: legacyBody,
                }
              : undefined,
            attempts: 0,
            status: 'pending',
            message: undefined,
            actual: undefined,
            dependencies: item.dependencies.map((parent) => replacements.get(parent) ?? parent),
            parentId: item.parentId ? (replacements.get(item.parentId) ?? item.parentId) : null,
            versionParents: item.versionParents
              ? Object.fromEntries(
                  Object.entries(item.versionParents).map(([scope, parent]) => [
                    scope,
                    replacements.get(parent) ?? parent,
                  ]),
                )
              : undefined,
            contextRevisions: item.contextRevisions
              ? Object.fromEntries(
                  Object.keys(item.contextRevisions).map((scope) => [
                    scope,
                    row.actual!.revisions[canonicalScope(scope, mappings)] ??
                      item.contextRevisions![scope],
                  ]),
                )
              : undefined,
            expectedRevision: item.legacy
              ? null
              : canonicalScope(item.scope, mappings) === canonicalScope(row.scope, mappings)
                ? (row.actual.revisions[canonicalScope(item.scope, mappings)] ?? null)
                : item.expectedRevision,
          });
        }
      }
    },
  );
  await getBundle(owner);
  notifyOfflineChange();
}
export async function discardPendingOperation(id: string) {
  const owner = currentOwner();
  if (owner == null) throw new Error('La sesión ha cambiado.');
  await offlineDb.transaction('rw', offlineDb.mutations, async () => {
    const rows = await rowsFor(owner);
    if (!rows.some((o) => o.id === id))
      throw new Error('El cambio pendiente no pertenece a esta sesión.');
    const chain = dependentChain(rows, id);
    if (chain.some((o) => o.status === 'sending'))
      throw new Error('Espera a que termine la sincronización antes de descartar.');
    for (const row of chain) await offlineDb.mutations.delete(row.id);
  });
  notifyOfflineChange();
}
export async function exportPendingOperations() {
  const owner = currentOwner();
  return JSON.stringify(
    { schema_version: 1, owner_id: owner, operations: owner == null ? [] : await rowsFor(owner) },
    null,
    2,
  );
}
export async function listPendingOperations() {
  const owner = currentOwner();
  if (owner == null) return [];
  const rows = await rowsFor(owner);
  const bundle = await getBundle(owner);
  if (!bundle) return rows;
  const context = await projected(owner, bundle);
  return rows.map((operation) => {
    const projectionError = context.projectionErrors.get(operation.id);
    const awaitingReceipt =
      projectionError &&
      ['sending', 'retry'].includes(operation.status) &&
      operation.attempts > 0 &&
      operation.payload;
    return {
      ...operation,
      message:
        operation.message ??
        (awaitingReceipt
          ? 'Confirmación pendiente. Se comprobará al reintentar.'
          : projectionError),
    };
  });
}
export const resolveConflict = resolveOperationConflict;
const EMPTY_ENTITY_MAPPINGS: IdMapping[] = [];
export function useEntityMappings(): IdMapping[] {
  const owner = useAuthStore((s) => s.user?.id ?? null);
  const result = useLiveQuery(async () => {
    if (owner == null || !offlineDb.mappings)
      return { owner, explotacionId: null, mappings: EMPTY_ENTITY_MAPPINGS };
    const bundle = await getBundle(owner);
    const mappings = (await offlineDb.mappings.where('ownerId').equals(owner).toArray()).filter(
      (mapping) => mapping.ownerId === owner && mapping.explotacionId === bundle?.explotacion_id,
    );
    return { owner, explotacionId: bundle?.explotacion_id ?? null, mappings };
  }, [owner]);
  return result?.owner === owner &&
    result.explotacionId === (currentBundle()?.explotacion_id ?? null)
    ? result.mappings
    : EMPTY_ENTITY_MAPPINGS;
}
export function useEntityId(
  value: string | number | null | undefined,
  entity?: string,
): number | null {
  const id = parseEntityId(value == null ? value : String(value));
  const owner = useAuthStore((s) => s.user?.id ?? null);
  const mappings = useEntityMappings();
  if (id == null || !Number.isSafeInteger(id) || id === 0) return null;
  return (
    mappings?.find(
      (mapping) =>
        mapping.ownerId === owner &&
        mapping.localId === id &&
        (!entity || mapping.entity === entity),
    )?.remoteId ?? id
  );
}
export function usePendingOperations() {
  const owner = useAuthStore((s) => s.user?.id ?? null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const changed = () => setVersion((v) => v + 1);
    window.addEventListener('offline:change', changed);
    window.addEventListener('offline', changed);
    window.addEventListener('online', changed);
    return () => {
      window.removeEventListener('offline:change', changed);
      window.removeEventListener('offline', changed);
      window.removeEventListener('online', changed);
    };
  }, []);
  const result = useLiveQuery(async () => {
    try {
      const bundle = await getBundle();
      return {
        owner,
        operations: owner == null ? [] : await listPendingOperations(),
        available: true,
        error: null as string | null,
        preparedAt: bundle?.prepared_at ?? null,
      };
    } catch (error) {
      return {
        owner,
        operations: [] as PendingOperation[],
        available: false,
        error: extractApiMessage(error),
        preparedAt: null,
      };
    }
  }, [owner, version]);
  return {
    operations:
      result?.owner === owner ? (result?.operations ?? []).filter((o) => o.ownerId === owner) : [],
    available: result?.available ?? typeof indexedDB !== 'undefined',
    error: result?.error ?? getStorageError(),
    busy,
    preparedAt: result?.owner === owner ? result?.preparedAt : null,
    preparing: isPreparing(),
    connection: navigator.onLine === false ? 'offline' : connection,
  };
}
export function startOperationsSync(onChange?: () => void) {
  connectOfflineChannel();
  const sync = () => void syncPendingOperations().catch(() => {});
  const foreground = () => {
    void syncPendingOperations()
      .then(() => prepareOfflineBundle())
      .catch(() => {});
  };
  const visibility = () => {
    if (document.visibilityState === 'visible') foreground();
  };
  const storage = (event: StorageEvent) => {
    if (event.key?.startsWith('camadero.')) useAuthStore.getState().loadFromStorage();
  };
  window.addEventListener('online', foreground);
  window.addEventListener('focus', foreground);
  document.addEventListener('visibilitychange', visibility);
  window.addEventListener('storage', storage);
  window.addEventListener('offline:queued', sync);
  if (onChange) window.addEventListener('offline:change', onChange);
  const unsubscribe = useAuthStore.subscribe((s, p) => {
    if (s.user?.id !== p.user?.id || s.bump !== p.bump) foreground();
  });
  const timer = window.setInterval(() => {
    if (document.visibilityState === 'visible') sync();
  }, 30_000);
  foreground();
  return () => {
    window.clearInterval(timer);
    unsubscribe();
    window.removeEventListener('online', foreground);
    window.removeEventListener('focus', foreground);
    window.removeEventListener('offline:queued', sync);
    document.removeEventListener('visibilitychange', visibility);
    window.removeEventListener('storage', storage);
    if (onChange) window.removeEventListener('offline:change', onChange);
  };
}
